// A single, page-wide IntersectionObserver shared by every ProductCard.
// One instance watches many card elements; each card fires its in-view
// callback once, then is unobserved.

let observer: IntersectionObserver | null = null;
const callbacks = new WeakMap<Element, () => void>();

// Treat "effectively fully visible" as in-view. Exact 1.0 can never be
// reached due to sub-pixel rounding, so wake up just below full visibility.
const IN_VIEW_RATIO = 0.99;

function handleEntries(entries: IntersectionObserverEntry[]): void {
  for (const entry of entries) {
    if (entry.intersectionRatio < IN_VIEW_RATIO) continue;
    callbacks.get(entry.target)?.();
    observer?.unobserve(entry.target);
    callbacks.delete(entry.target);
  }
}

function getObserver(): IntersectionObserver | null {
  if (typeof IntersectionObserver === "undefined") return null;
  if (observer === null) {
    observer = new IntersectionObserver(handleEntries, {
      threshold: [IN_VIEW_RATIO, 1],
    });
  }
  return observer;
}

/**
 * Observe an element and invoke `onInView` the first time it becomes ~100%
 * visible. Returns a cleanup function that stops observing the element.
 */
export function observeCard(
  element: Element,
  onInView: () => void,
): () => void {
  const obs = getObserver();
  if (!obs) return () => {};
  callbacks.set(element, onInView);
  obs.observe(element);
  return () => {
    obs.unobserve(element);
    callbacks.delete(element);
  };
}
